import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import Pagination from '@mui/material/Pagination';
import Skeleton from '@mui/material/Skeleton';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { Badge } from '@tinyrewards/shared';
import { getBadges, deleteBadge, setActiveBadge } from '../api/badges';
import BadgeCard from '../components/BadgeCard';

const PAGE_SIZE = 24;

type SortField = 'createddate' | 'name' | 'status';
type SortOrder = 'asc' | 'desc';
type Visibility = 'all' | 'public' | 'private';
type ActiveFilter = 'all' | 'active' | 'inactive';

function SkeletonCard() {
    return (
        <Card sx={{ height: '100%' }}>
            <Skeleton variant="rectangular" height={160} />
            <CardContent>
                <Skeleton width="70%" height={28} />
                <Skeleton width="90%" />
                <Skeleton width="60%" />
            </CardContent>
        </Card>
    );
}

export default function MyBadgesPage() {
    const navigate = useNavigate();
    const [badges, setBadges] = useState<Badge[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sort, setSort] = useState<SortField>('createddate');
    const [order, setOrder] = useState<SortOrder>('desc');
    const [visibility, setVisibility] = useState<Visibility>('all');
    const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');
    const [snackbar, setSnackbar] = useState('');
    const [deleteTarget, setDeleteTarget] = useState<Badge | null>(null);
    const [deleting, setDeleting] = useState(false);

    // Debounce search input
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 350);
        return () => clearTimeout(t);
    }, [search]);

    const load = useCallback((p: number, s: string, sf: SortField, so: SortOrder, v: Visibility, af: ActiveFilter) => {
        setLoading(true);
        setError(null);
        getBadges({ page: p, pageSize: PAGE_SIZE, search: s || undefined, sort: sf, order: so, visibility: v, activeFilter: af })
            .then((res) => {
                if (res.success) {
                    setBadges(res.data ?? []);
                    setTotal(res.total ?? 0);
                } else {
                    setError(res.message ?? 'Failed to load badges.');
                }
            })
            .catch(() => setError('An unexpected error occurred.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        load(page, debouncedSearch, sort, order, visibility, activeFilter);
    }, [page, debouncedSearch, sort, order, visibility, activeFilter, load]);

    const resetFilters = () => {
        setSearch('');
        setVisibility('all');
        setActiveFilter('all');
        setPage(1);
    };

    const handleToggleActive = async (badge: Badge) => {
        const newActive = !badge.isActive;
        const res = await setActiveBadge(badge.badgeID, newActive);
        if (res.success) {
            setSnackbar(`Badge ${newActive ? 'activated' : 'deactivated'}.`);
            // If filtered to active/inactive, reload so the toggled badge moves out of view
            if (activeFilter !== 'all') {
                load(page, debouncedSearch, sort, order, visibility, activeFilter);
            } else {
                setBadges(prev => prev.map(b => b.badgeID === badge.badgeID ? { ...b, isActive: newActive } : b));
            }
        } else {
            setSnackbar(res.message ?? 'Failed to update badge status.');
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const res = await deleteBadge(deleteTarget.badgeID);
            if (res.success) {
                setSnackbar('Badge deleted.');
                load(page, debouncedSearch, sort, order, visibility, activeFilter);
            } else {
                setSnackbar(res.message ?? 'Failed to delete badge.');
            }
        } catch {
            setSnackbar('An unexpected error occurred.');
        } finally {
            setDeleting(false);
            setDeleteTarget(null);
        }
    };

    const totalPages = Math.ceil(total / PAGE_SIZE);

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <Box mb={3}>
                <Typography variant="h4" fontWeight={700}>Manage Badges</Typography>
                {!loading && (
                    <Typography variant="body2" color="text.secondary">
                        {total} badge{total !== 1 ? 's' : ''}
                    </Typography>
                )}
            </Box>

            {/* Filters */}
            <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
                <TextField
                    size="small"
                    placeholder="Search by name or description…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    sx={{ minWidth: 260 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Visibility</InputLabel>
                    <Select
                        label="Visibility"
                        value={visibility}
                        onChange={e => { setVisibility(e.target.value as Visibility); setPage(1); }}
                    >
                        <MenuItem value="all">All badges</MenuItem>
                        <MenuItem value="public">Public only</MenuItem>
                        <MenuItem value="private">Private only</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        label="Status"
                        value={activeFilter}
                        onChange={e => { setActiveFilter(e.target.value as ActiveFilter); setPage(1); }}
                    >
                        <MenuItem value="all">All statuses</MenuItem>
                        <MenuItem value="active">Active only</MenuItem>
                        <MenuItem value="inactive">Inactive only</MenuItem>
                    </Select>
                </FormControl>
                <Box display="flex" alignItems="center" gap={1}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Sort by</InputLabel>
                        <Select
                            label="Sort by"
                            value={sort}
                            onChange={e => { setSort(e.target.value as SortField); setPage(1); }}
                        >
                            <MenuItem value="createddate">Created date</MenuItem>
                            <MenuItem value="name">Name</MenuItem>
                            <MenuItem value="status">Status</MenuItem>
                        </Select>
                    </FormControl>
                    <IconButton
                        size="small"
                        title={order === 'desc' ? 'Descending' : 'Ascending'}
                        onClick={() => { setOrder(o => o === 'desc' ? 'asc' : 'desc'); setPage(1); }}
                    >
                        {order === 'desc' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
                    </IconButton>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Grid */}
            {loading ? (
                <Grid container spacing={3}>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}>
                            <SkeletonCard />
                        </Grid>
                    ))}
                </Grid>
            ) : badges.length === 0 ? (
                <Box textAlign="center" py={8} color="text.secondary">
                    <WorkspacePremiumIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                    <Typography mt={2}>
                        {search || visibility !== 'all' || activeFilter !== 'all'
                            ? 'No badges match your filters.'
                            : 'No badges yet. Create your first badge to get started.'}
                    </Typography>
                    {(search || visibility !== 'all' || activeFilter !== 'all') && (
                        <Button onClick={resetFilters} sx={{ mt: 1 }}>Clear filters</Button>
                    )}
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {badges.map((badge) => (
                        <Grid item xs={12} sm={6} md={4} key={badge.badgeID}>
                            <BadgeCard badge={badge} showStatus onView={(b) => navigate(`/badges/${b.badgeID}`)} onEdit={(b) => navigate(`/badges/${b.badgeID}/edit`)} onAccess={(b) => navigate(`/badges/${b.badgeID}/access`)} onDelete={setDeleteTarget} onToggleActive={handleToggleActive} />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Pagination */}
            {!loading && totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={4}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, p) => setPage(p)}
                        color="primary"
                    />
                </Box>
            )}
            {/* Delete confirmation */}
            <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)}>
                <DialogTitle>Delete Badge</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete <strong>{deleteTarget?.badgeName}</strong>? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" disabled={deleting}>
                        {deleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!snackbar}
                autoHideDuration={4000}
                onClose={() => setSnackbar('')}
                message={snackbar}
            />
        </Container>
    );
}
